import { Injectable, isDevMode } from '@angular/core';
import { AdminAuthStorageKeys } from '../../enums/admin-auth-storage-keys.enum';
import { safeErrorSummary } from '../../helper/safe-error-summary';

interface SessionKeyMaterialI {
  cryptoKey: CryptoKey;
  keyId: string;
}

interface SecureEnvelopeI {
  keyId: string;
  data: string;
}

/**
 * Service for encrypting and decrypting data stored in localStorage
 * Uses Web Crypto API for secure encryption
 */
@Injectable({
  providedIn: 'root',
})
export class SecureStorageService {
  private readonly ALGORITHM = 'AES-GCM';
  private readonly KEY_LENGTH = 256;
  private readonly IV_LENGTH = 12; // 96 bits for AES-GCM

  /**
   * Logical keys `setSecureItem` has written under during this tab's
   * lifetime. `clearSessionKey` (#2991) uses this set to know which
   * namespaced slots to sweep without hardcoding any particular caller's
   * key names here — `SecureStorageService` is `providedIn: 'root'`, so
   * this survives for as long as the singleton does (effectively the whole
   * tab session, i.e. from the first `setSecureItem` call through logout).
   */
  private readonly writtenKeys = new Set<string>();

  /**
   * Dev-only logging. Suppressed in production builds so the plaintext data
   * being encrypted is never written to the browser console of a deployed app.
   */
  private debug(...args: unknown[]): void {
    if (isDevMode()) {
      console.warn(...args);
    }
  }

  /**
   * Generates a cryptographic key for encryption/decryption
   */
  private async generateKey(): Promise<CryptoKey> {
    return await crypto.subtle.generateKey(
      {
        name: this.ALGORITHM,
        length: this.KEY_LENGTH,
      },
      true, // extractable
      ['encrypt', 'decrypt'],
    );
  }

  /**
   * Derives a key from a password using PBKDF2
   */
  private async deriveKey(password: string, salt: ArrayBuffer): Promise<CryptoKey> {
    const encoder = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey('raw', encoder.encode(password), { name: 'PBKDF2' }, false, [
      'deriveKey',
    ]);

    return await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: 100000, // 100k iterations for security
        hash: 'SHA-256',
      },
      keyMaterial,
      { name: this.ALGORITHM, length: this.KEY_LENGTH },
      false,
      ['encrypt', 'decrypt'],
    );
  }

  /**
   * Physical localStorage key for a logical `key`, namespaced by the session
   * key's `keyId`.
   *
   * This is the chosen fix for two admin tabs colliding on one shared slot:
   * `sessionStorage` (and therefore the session key + `keyId`) is per-tab,
   * but `localStorage` is shared, so a single un-namespaced slot means tab A
   * and tab B fight over the same key — whichever reads last deletes
   * whichever wrote last. Namespacing the physical key by `keyId` gives each
   * tab its OWN slot: both can hold a live, decryptable cache at the same
   * time, and neither tab's read can ever observe (or remove) the other's
   * entry. The cheaper alternative — one shared slot, simply never
   * `removeItem` on a `keyId` mismatch — was rejected because the tabs would
   * still stomp each other's cache on every write; namespacing is the only
   * one of the two that gives both tabs a genuinely live cache at once.
   */
  private storageKeyFor(key: string, keyId: string): string {
    return `${key}:${keyId}`;
  }

  /**
   * Best-effort, synchronous peek at the `keyId` embedded in the current
   * session key blob, without importing or generating anything. Lets
   * `removeSecureItem` stay synchronous — it only needs the string to compute
   * which namespaced slot to clear, never a usable `CryptoKey`.
   */
  private peekCurrentKeyId(): string | null {
    const sessionKeyData = sessionStorage.getItem(AdminAuthStorageKeys.SESSION_KEY);
    if (!sessionKeyData) {
      return null;
    }
    try {
      const parsed: unknown = JSON.parse(sessionKeyData);
      const keyId = (parsed as Partial<{ keyId: unknown }> | null)?.keyId;
      return typeof keyId === 'string' ? keyId : null;
    } catch {
      return null;
    }
  }

  /**
   * Gets or creates a session-based encryption key, paired with a `keyId`
   * generated at the same time and stored alongside it in sessionStorage.
   * `setSecureItem`/`getSecureItem` namespace every localStorage entry under
   * this same `keyId` (see `storageKeyFor`) so two tabs with different
   * session keys never share a physical slot.
   *
   * Also transparently migrates the pre-#2913 session blob format — a bare
   * `number[]` of raw key bytes, with no `keyId` at all. The key bytes are
   * still good, so they're imported as-is and paired with a freshly
   * generated `keyId`, written back in the new `{ key, keyId }` shape so the
   * SAME `keyId` is reused for the rest of this tab's session instead of
   * being regenerated (and re-migrated) on every call.
   */
  private async getSessionKeyMaterial(): Promise<SessionKeyMaterialI> {
    const sessionKeyData = sessionStorage.getItem(AdminAuthStorageKeys.SESSION_KEY);

    if (sessionKeyData) {
      try {
        const parsed: unknown = JSON.parse(sessionKeyData);

        if (Array.isArray(parsed)) {
          // Pre-#2913 bare number[] format — no keyId was ever assigned.
          const cryptoKey = await crypto.subtle.importKey(
            'raw',
            new Uint8Array(parsed),
            { name: this.ALGORITHM },
            false,
            ['encrypt', 'decrypt'],
          );
          const keyId = crypto.randomUUID();
          sessionStorage.setItem(AdminAuthStorageKeys.SESSION_KEY, JSON.stringify({ key: parsed, keyId }));
          return { cryptoKey, keyId };
        }

        const candidate = parsed as Partial<{ key: number[]; keyId: string }> | null;
        if (!candidate || !Array.isArray(candidate.key) || typeof candidate.keyId !== 'string') {
          throw new Error('Not a valid session key blob');
        }

        const cryptoKey = await crypto.subtle.importKey(
          'raw',
          new Uint8Array(candidate.key),
          { name: this.ALGORITHM },
          false,
          ['encrypt', 'decrypt'],
        );
        return { cryptoKey, keyId: candidate.keyId };
      } catch {
        // If parsing/import fails, generate new key
      }
    }

    // Generate new key + keyId together and store both in session
    const key = await this.generateKey();
    const exportedKey = await crypto.subtle.exportKey('raw', key);
    const keyId = crypto.randomUUID();
    sessionStorage.setItem(
      AdminAuthStorageKeys.SESSION_KEY,
      JSON.stringify({ key: Array.from(new Uint8Array(exportedKey)), keyId }),
    );

    return { cryptoKey: key, keyId };
  }

  /**
   * Encrypts data and stores it in localStorage
   */
  async setSecureItem<T>(key: string, data: T): Promise<void> {
    try {
      this.debug('SecureStorageService: Starting encryption for key:', key);
      // #2249: never print the plaintext being encrypted — the whole point of this service.

      const encoder = new TextEncoder();
      const dataString = JSON.stringify(data);
      const dataBuffer = encoder.encode(dataString);

      this.debug('SecureStorageService: Getting session key...');
      const { cryptoKey, keyId } = await this.getSessionKeyMaterial();
      const iv = crypto.getRandomValues(new Uint8Array(this.IV_LENGTH));

      const encryptedData = await crypto.subtle.encrypt(
        {
          name: this.ALGORITHM,
          iv: iv,
        },
        cryptoKey,
        dataBuffer,
      );

      // Combine IV and encrypted data
      const combined = new Uint8Array(iv.length + encryptedData.byteLength);
      combined.set(iv, 0);
      combined.set(new Uint8Array(encryptedData), iv.length);

      // Store as base64, tagged with — and namespaced under (see
      // `storageKeyFor`) — the keyId of the session key that encrypted it
      const base64Data = btoa(String.fromCharCode(...combined));
      const envelope: SecureEnvelopeI = { keyId, data: base64Data };
      localStorage.setItem(this.storageKeyFor(key, keyId), JSON.stringify(envelope));
      // #2991: recorded so `clearSessionKey` can sweep this key's stale slots
      // later — never swept HERE, on every write, because a second tab can be
      // genuinely live under a different keyId at the same moment (see the
      // "multi-tab keyId namespacing" spec below); pruning on write would
      // delete that tab's live cache, the exact collision this namespacing
      // exists to prevent.
      this.writtenKeys.add(key);
      this.debug('SecureStorageService: Successfully encrypted and stored data for key:', key);
    } catch (error) {
      console.error('SecureStorageService: Error encrypting data:', safeErrorSummary(error));
      throw new Error('Failed to encrypt and store data');
    }
  }

  /**
   * Decrypts a base64 `iv || ciphertext` blob under the given key. Shared by
   * the matching-envelope path and the legacy-migration path in
   * `getSecureItem` below.
   */
  private async decryptPayload<T>(base64Data: string, cryptoKey: CryptoKey): Promise<T> {
    const combined = new Uint8Array(
      atob(base64Data)
        .split('')
        .map(char => char.charCodeAt(0)),
    );

    const iv = combined.slice(0, this.IV_LENGTH);
    const encrypted = combined.slice(this.IV_LENGTH);

    const decryptedData = await crypto.subtle.decrypt(
      {
        name: this.ALGORITHM,
        iv: iv,
      },
      cryptoKey,
      encrypted,
    );

    const decoder = new TextDecoder();
    const dataString = decoder.decode(decryptedData);
    return JSON.parse(dataString) as T;
  }

  /**
   * Retrieves and decrypts data from localStorage.
   *
   * Entries are namespaced per session `keyId` (see `storageKeyFor`), so a
   * missing/rotated session key (new tab, browser restart) simply means
   * nothing lives at THIS tab's slot — a clean cache miss, never an error —
   * and it is never a reason to touch whatever another tab's `keyId` has
   * stored at ITS slot.
   *
   * Two pre-#2913 legacy shapes are read transparently, once, and migrated
   * into the namespaced envelope so they're never re-decrypted or re-probed
   * again:
   *  - a bare `number[]` session key blob (migrated by `getSessionKeyMaterial`)
   *  - a bare base64 ciphertext with no envelope, stored under the plain
   *    (un-namespaced) key — migrated below.
   *
   * The whole method is wrapped so it can never reject: the declared
   * `Promise<T | null>` contract holds even if `getSessionKeyMaterial` itself
   * throws (e.g. `crypto.subtle.generateKey` failing, or a `sessionStorage`
   * write hitting `QuotaExceededError`) — that is a cache miss too, not a
   * rejection the caller has to remember to catch.
   */
  async getSecureItem<T>(key: string): Promise<T | null> {
    try {
      const { cryptoKey, keyId } = await this.getSessionKeyMaterial();
      const namespacedKey = this.storageKeyFor(key, keyId);

      const rawEntry = localStorage.getItem(namespacedKey);
      if (rawEntry) {
        let envelope: SecureEnvelopeI;
        try {
          const parsed = JSON.parse(rawEntry) as Partial<SecureEnvelopeI>;
          if (typeof parsed.keyId !== 'string' || typeof parsed.data !== 'string' || parsed.keyId !== keyId) {
            throw new Error('Not a valid secure storage envelope');
          }
          envelope = parsed as SecureEnvelopeI;
        } catch (error) {
          // This tab's OWN namespaced slot should always hold a well-formed
          // envelope tagged with this exact keyId — this branch means it
          // doesn't, which is corruption of our own entry, not orphaning, so
          // it's safe to clear.
          this.debug('SecureStorageService: unreadable entry for key, clearing:', key, safeErrorSummary(error));
          localStorage.removeItem(namespacedKey);
          return null;
        }

        try {
          return await this.decryptPayload<T>(envelope.data, cryptoKey);
        } catch (error) {
          console.error('Error decrypting data:', safeErrorSummary(error));
          return null;
        }
      }

      // Nothing at this tab's namespaced slot yet — check for a pre-#2913
      // legacy entry (bare base64, no envelope) left under the plain,
      // un-namespaced key.
      const legacyEntry = localStorage.getItem(key);
      if (!legacyEntry) {
        return null;
      }

      try {
        const result = await this.decryptPayload<T>(legacyEntry, cryptoKey);
        const migratedEnvelope: SecureEnvelopeI = { keyId, data: legacyEntry };
        localStorage.setItem(namespacedKey, JSON.stringify(migratedEnvelope));
        // P3-2 (VR-3004): this is a write too — record it so a later
        // `clearSessionKey` can sweep this key's stale slots, the same as
        // any `setSecureItem` write.
        this.writtenKeys.add(key);
        localStorage.removeItem(key);
        return result;
      } catch {
        // Not decryptable under the CURRENT session key either. A legacy
        // entry carries no keyId, so this can't be checked deterministically
        // the way a namespaced mismatch can — but it's still the ordinary
        // "orphan from a session that no longer exists" case rather than
        // corruption, so it gets the same silent cache-miss treatment.
        this.debug('SecureStorageService: legacy entry for key could not be migrated, clearing:', key);
        localStorage.removeItem(key);
        return null;
      }
    } catch (error) {
      this.debug('SecureStorageService: getSecureItem failed, treating as cache miss:', key, safeErrorSummary(error));
      return null;
    }
  }

  /**
   * Removes encrypted item from localStorage — both this tab's namespaced
   * slot and any leftover pre-#2913 legacy (un-namespaced) entry.
   *
   * P3-2 (VR-3004): also sweeps this key's other stale namespaced slots
   * (see `pruneStaleSlots`). Unlike `clearSessionKey`'s sweep, this does NOT
   * depend on `writtenKeys` — it already has `key` and the current `keyId`
   * in hand, so it collects stale slots even for a tab that only ever
   * *read* this key (never called `setSecureItem` for it), and even for the
   * legacy-migration write in `getSecureItem` below.
   */
  removeSecureItem(key: string): void {
    localStorage.removeItem(key);
    const keyId = this.peekCurrentKeyId();
    if (keyId) {
      localStorage.removeItem(this.storageKeyFor(key, keyId));
      this.pruneStaleSlots(key, keyId);
    }
  }

  /**
   * #2991: `storageKeyFor` gives every session's write to `key` its own
   * permanent localStorage slot, and nothing ever collected a PRIOR
   * session's slot — every stale one is undecryptable (that session's key
   * died with `sessionStorage`) but stays on disk forever. Removes every
   * slot under `key` except the one tagged with `currentKeyId` — but only
   * when the slot's own stored value verifies as a `SecureEnvelopeI` whose
   * `keyId` matches the slot's own key suffix (P3-1, VR-3004): the sweep
   * has no service-owned namespace to trust (`storageKeyFor` is just
   * `` `${key}:${keyId}` ``), so this makes the blast radius provably the
   * service's own writes — a foreign entry that merely shares this key's
   * `${key}:` prefix (e.g. `_adminTenant:draft` written by something else)
   * is left alone.
   *
   * Only ever called from `clearSessionKey` (logout) and `removeSecureItem`
   * — both strong, explicit "this session/key is over" signals — never from
   * `setSecureItem`: two tabs can be genuinely live under different
   * `keyId`s at once, and there is no way to see that from here
   * (`sessionStorage` is per-tab), so a sweep triggered by every write would
   * delete a second tab's live cache.
   *
   * VR-3004 P2-1: triggering it on logout does NOT keep that "two tabs stay
   * live" invariant intact — it deliberately breaks it. If a second tab is
   * still open under a different `keyId` for this same `key` when this tab
   * logs out, that tab's genuinely live slot is swept too; there is still no
   * way to see from here that it's live rather than stale. This is an
   * accepted trade, not an oversight: the other tab's next read simply
   * misses its namespaced slot and falls through the existing #2913
   * orphan-as-cache-miss contract in `getSecureItem` (the same path a
   * rotated/foreign `keyId` already takes) — never a decryption error,
   * never stale data, just one extra cache miss that re-resolves normally.
   * Bounding #2991's unbounded growth on every explicit logout was judged
   * worth that trade.
   */
  private pruneStaleSlots(key: string, currentKeyId: string): void {
    const currentSlot = this.storageKeyFor(key, currentKeyId);
    const prefix = `${key}:`;
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const storageKey = localStorage.key(i);
      if (!storageKey || !storageKey.startsWith(prefix) || storageKey === currentSlot) {
        continue;
      }
      const rawValue = localStorage.getItem(storageKey);
      if (!rawValue) {
        continue;
      }
      const suffixKeyId = storageKey.slice(storageKey.lastIndexOf(':') + 1);
      try {
        const parsed = JSON.parse(rawValue) as Partial<SecureEnvelopeI>;
        if (parsed.keyId === suffixKeyId && typeof parsed.data === 'string') {
          localStorage.removeItem(storageKey);
        }
      } catch {
        // Not a SecureEnvelopeI this service wrote — a foreign same-prefix
        // entry. Leave it alone (P3-1).
      }
    }
  }

  /**
   * Clears the session encryption key (call on logout).
   *
   * #2991: also sweeps every namespaced localStorage slot this tab has ever
   * written to (`writtenKeys`), dropping any that don't belong to the
   * session key being cleared here — including, per VR-3004 P2-1, a second
   * tab's still-live slot for the same key (see `pruneStaleSlots`'s doc
   * comment for why that trade is accepted). Keeps the #2913
   * orphan-as-cache-miss contract in `getSecureItem` untouched either way —
   * this only ever deletes, it never changes what a mismatched read
   * returns.
   */
  clearSessionKey(): void {
    const keyId = this.peekCurrentKeyId();
    if (keyId) {
      for (const key of this.writtenKeys) {
        this.pruneStaleSlots(key, keyId);
      }
    }
    sessionStorage.removeItem(AdminAuthStorageKeys.SESSION_KEY);
  }
}
